import { cn } from '@/lib/cn'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import {
  buttonClasses,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children?: ReactNode
}

/**
 * Defaults to type="button": a bare <button> inside a form submits it, which
 * turns every in-form action (add line, back, change) into an unintended save.
 * Submit is opt-in via type="submit".
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  type = 'button',
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'disabled:cursor-not-allowed',
        buttonClasses(variant, size, className),
      )}
    >
      {loading ? (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {children}
    </button>
  )
}

/** Icon-only action for table rows and other dense layouts. */
export function IconButton({
  label,
  variant = 'ghost',
  className,
  children,
  ...rest
}: {
  label: string
  variant?: 'ghost' | 'danger'
  className?: string
  children?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      type="button"
      aria-label={label}
      title={label}
      variant="ghost"
      size="icon-sm"
      className={cn(
        'text-ink-faint',
        variant === 'danger' && 'hover:bg-critical-soft hover:text-critical',
        className,
      )}
      {...rest}
    >
      {children}
    </Button>
  )
}
